// Image Compression Utilities for 3G optimization
export const compressImage = (file, options = {}) => {
  const {
    maxWidth = 800,
    maxHeight = 800,
    quality = 0.7,
    outputFormat = 'image/jpeg',
  } = options;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      try {
        let { width, height } = img;
        const aspectRatio = width / height;

        if (width > maxWidth) {
          width = maxWidth;
          height = width / aspectRatio;
        }
        if (height > maxHeight) {
          height = maxHeight;
          width = height * aspectRatio;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        const compressedDataUrl = canvas.toDataURL(outputFormat, quality);
        const base64Data = compressedDataUrl.split(',')[1];
        const compressedSize = Math.round(base64Data.length * 0.75);
        const compressionRatio = Math.round(
          ((file.size - compressedSize) / file.size) * 100,
        );

        URL.revokeObjectURL(img.src);

        resolve({
          base64Data,
          compressedSize,
          compressionRatio,
          originalSize: file.size,
          dimensions: { width, height },
        });
      } catch (error) {
        reject(new Error(`Image compression failed: ${error.message}`));
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for compression'));
    };

    img.src = URL.createObjectURL(file);
  });
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export const validateImageType = (
  file,
  acceptedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/heic'],
) => {
  return acceptedTypes.includes(file.type);
};

export const isWebView = () => {
  const userAgent = navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
  const isAndroid = /Android/i.test(userAgent);

  // Check for various webview indicators
  const webViewIndicators = [
    'wv', // Android WebView
    'WebView', // Generic WebView
    'Mobile/', // iOS WebView
    'Version/', // iOS WebView with Safari version
  ];

  const hasWebViewIndicator = webViewIndicators.some((indicator) =>
    userAgent.includes(indicator),
  );

  // iOS WebView detection
  const isIOSWebView =
    isIOS &&
    !userAgent.includes('Safari/') &&
    (userAgent.includes('Mobile/') || userAgent.includes('Version/'));

  // Android WebView detection
  const isAndroidWebView =
    isAndroid && (userAgent.includes('wv') || !userAgent.includes('Chrome/'));

  return hasWebViewIndicator || isIOSWebView || isAndroidWebView;
};

export const estimateNetworkFromUA = () => {
  const userAgent = navigator.userAgent || '';

  // Check for specific mobile indicators that suggest slower networks
  if (/2G|EDGE|GPRS/i.test(userAgent)) return '2g';
  if (/3G|HSPA|UMTS/i.test(userAgent)) return '3g';
  if (/4G|LTE/i.test(userAgent)) return '4g';
  if (/5G/i.test(userAgent)) return '5g';

  // Default estimation based on device type
  const isMobile = /Mobile|Android|iPhone|iPad/i.test(userAgent);
  return isMobile ? '3g' : '4g'; // Conservative estimate
};

export const performConnectionTest = async () => {
  try {
    const startTime = performance.now();
    const testUrl =
      'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'; // 1x1 transparent gif

    await fetch(testUrl, { method: 'HEAD', cache: 'no-cache' });
    const endTime = performance.now();
    const latency = endTime - startTime;

    // Rough network type estimation based on latency
    if (latency > 1000) return '2g';
    if (latency > 500) return '3g';
    if (latency > 100) return '4g';
    return '5g';
  } catch (error) {
    return 'unknown';
  }
};

export const getNetworkType = async () => {
  // Primary: Use Network Information API if available
  if (navigator.connection && navigator.connection.effectiveType) {
    const effectiveType = navigator.connection.effectiveType;
    if (effectiveType && effectiveType !== 'unknown') {
      return effectiveType;
    }
  }

  // Secondary: For WebView environments, use multiple fallback methods
  if (isWebView()) {
    // Try user agent detection first
    const uaNetwork = estimateNetworkFromUA();
    if (uaNetwork !== 'unknown') {
      return uaNetwork;
    }

    // Fallback to connection test
    try {
      return await performConnectionTest();
    } catch (error) {
      console.warn('Network detection failed, using conservative default');
      return '3g'; // Conservative default for webviews
    }
  }

  // Tertiary: Try connection test for other environments
  try {
    return await performConnectionTest();
  } catch (error) {
    // Final fallback
    const isMobile = /Mobile|Android|iPhone|iPad/i.test(
      navigator.userAgent || '',
    );
    return isMobile ? '3g' : '4g';
  }
};

export const getOptimalCompressionSettings = async (networkType = null) => {
  const detectedNetworkType = networkType || (await getNetworkType());

  switch (detectedNetworkType) {
    case '2g':
    case 'slow-2g':
      return { maxWidth: 640, maxHeight: 640, quality: 0.5 };
    case '3g':
      return { maxWidth: 640, maxHeight: 640, quality: 0.6 };
    case '4g':
      return { maxWidth: 1024, maxHeight: 1024, quality: 0.9 };
    case '5g':
      return { maxWidth: 1440, maxHeight: 1440, quality: 1 };
    default:
      return { maxWidth: 800, maxHeight: 800, quality: 0.9 };
  }
};

export const compressMultipleImages = async (files, options = {}) => {
  const results = [];

  for (const file of files) {
    try {
      const result = await compressImage(file, options);
      results.push({
        filename: file.name,
        success: true,
        ...result,
      });
    } catch (error) {
      results.push({
        filename: file.name,
        success: false,
        error: error.message,
      });
    }
  }

  return results;
};

const imageCompressionUtils = {
  compressImage,
  formatFileSize,
  validateImageType,
  isWebView,
  estimateNetworkFromUA,
  performConnectionTest,
  getNetworkType,
  getOptimalCompressionSettings,
  compressMultipleImages,
};

export default imageCompressionUtils;
